import React, { lazy, useEffect, useMemo, useState } from 'react';

import styled from 'styled-components';

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { StompSocketState } from '@stomp/stompjs';
import { Navigate, Route, Routes } from 'react-router-dom';

import UILoader from '@aquariuxfintech/aqx-trader-react-ui/loader';

import { ETenantId } from '@aqx-core/common/api/config';
import configs, { isSIT } from '@aqx-core/common/configs';
import { APP_ROUTES, SECURED_ROUTES } from '@aqx-core/common/constants/router';
import { useAccountSwitchedSnackbar } from '@aqx-core/common/hooks/switch-account/use-account-switch-snackbar';
import { useAccountDetails } from '@aqx-core/common/services/auth';
import { useCompanyAccess, useCompanyTenantId } from '@aqx-core/common/services/company/hooks';
import { socketService } from '@aqx-core/common/socket';

// import { isPROD } from '@aqx-core/common/configs';

const AssetsScreen = lazy(() => import('./assets'));
const DealerScreen = lazy(() => import('./dealer'));
const MainScreen = lazy(() => import('./main'));
const MarketsScreen = lazy(() => import('./markets'));
const SignalScreen = lazy(() => import('./signal'));
const TradeScreen = lazy(() => import('./trade'));
const NewsScreen = lazy(() => import('./news'));
const CalendarScreen = lazy(() => import('./calendar'));
const EducationScreen = lazy(() => import('./education'));
const CopyTradeScreen = lazy(() => import('./copy-trade'));
const SSOAuthenticatedScreen = lazy(() => import('./sso-authenticated'));
const QuoteboardScreen = lazy(() => import('./quoteboard'));

const SecuredRoutes: FCC = () => {
  const [connected, setConnected] = useState(false);

  const {
    hasDealerAccess,
    hasNewsAccess,
    hasQuoteboard,
    hasEducationalVideo,
    hasCalendarAccess,
    hasSignal,
    hasCopyTrade
  } = useCompanyAccess();
  const tenantId = useCompanyTenantId();
  const accountDetails = useAccountDetails();
  const { accountNumber } = accountDetails ?? {};

  const hasDealerAccountAccess = useMemo(() => {
    const { dealer } = accountDetails || {};
    return hasDealerAccess ? dealer : false;
  }, [hasDealerAccess, accountDetails]);

  const defaultRoute = useMemo(() => {
    const environment = configs.environment;

    // For SIT testing only
    if (isSIT(environment) && tenantId === ETenantId.CENTROID && hasQuoteboard) {
      return SECURED_ROUTES.QUOTE;
    }

    if (tenantId === ETenantId.TINSHING && hasQuoteboard) {
      return SECURED_ROUTES.QUOTE;
    }
    return SECURED_ROUTES.TRADE;
  }, [tenantId, hasQuoteboard]);

  useEffect(() => {
    socketService.connect((state) => {
      if (state === StompSocketState.OPEN) {
        setConnected(true);
      }
    });
  }, []);

  useEffect(() => {
    if (accountNumber) {
      datadogRum.setUser({
        id: accountNumber.toString(),
        tenantId
      });
      datadogLogs.setUser({
        id: accountNumber.toString(),
        tenantId
      });
    }

    return () => {
      // when user is logged out, clear DD user details
      datadogRum.clearUser();
      datadogLogs.clearUser();
    };
  }, [accountNumber, tenantId]);

  useAccountSwitchedSnackbar(connected);

  if (!connected) {
    return <UILoader absolute />;
  }

  return (
    <StyledSecuredRoutes>
      <StyledSecuredRoutesScreens>
        <Routes>
          <Route path={APP_ROUTES.HOME} element={<MainScreen />}>
            <Route index={true} element={<Navigate to={defaultRoute} replace={true} />} />
            <Route path={SECURED_ROUTES.TRADE} element={<TradeScreen />} />
            <Route path={SECURED_ROUTES.MARKETS} element={<MarketsScreen />} />
            <Route path={SECURED_ROUTES.ASSETS} element={<AssetsScreen />} />
            {hasSignal && <Route path={SECURED_ROUTES.SIGNAL} element={<SignalScreen />} />}
            {hasQuoteboard && <Route path={SECURED_ROUTES.QUOTE} element={<QuoteboardScreen />} />}
            {hasNewsAccess && <Route path={SECURED_ROUTES.NEWS} element={<NewsScreen />} />}
            {hasDealerAccountAccess && <Route path={SECURED_ROUTES.DEALER} element={<DealerScreen />} />}
            {hasCalendarAccess && <Route path={SECURED_ROUTES.CALENDAR} element={<CalendarScreen />} />}
            {hasEducationalVideo && <Route path={SECURED_ROUTES.EDUCATION} element={<EducationScreen />} />}
            {hasCopyTrade && <Route path={SECURED_ROUTES.COPY_TRADE} element={<CopyTradeScreen />} />}
          </Route>
          <Route path="*" element={<Navigate to={defaultRoute} replace={true} />} />
          <Route path={SECURED_ROUTES.PELICAN_SSO_AUTHENTICATED} element={<SSOAuthenticatedScreen />} />
        </Routes>
      </StyledSecuredRoutesScreens>
    </StyledSecuredRoutes>
  );
};

export default SecuredRoutes;

const StyledSecuredRoutes = styled.div`
  flex: 1;
  flex-direction: row;
`;

const StyledSecuredRoutesScreens = styled.div`
  flex: 1;
  height: 100%;
`;
